import { useState, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, Outlet } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import NexusOverlay from './components/NexusOverlay';
import { ImpersonationService } from './services/impersonationService';
import BasicLoadingScreen from './components/BasicLoadingScreen';
import { AppBadgeManager } from './components/AppBadgeManager';
import { GlobalTextSelectionAI } from './components/GlobalTextSelectionAI';
import GlobalLocationTracker from './components/GlobalLocationTracker';
import { NativePushRegistration } from './components/NativePushRegistration';
import AppUpdateBanner from './components/AppUpdateBanner';
import { RemoteSessionGuard } from './components/RemoteSessionGuard';
import { RemoteSessionProvider, useRemoteSessionSafe } from './contexts/RemoteSessionContext';
import { useAiTokenCheckoutReturn } from './lib/useAiTokenCheckoutReturn';
import { useEmailCampaignCheckoutReturn } from './lib/useEmailCampaignCheckoutReturn';

import { 
  Package2, 
  Users, 
  FileText, 
  FileBarChart2, 
  Building2, 
  ChevronDown, 
  Plus, 
  Menu, 
  Search,
  X,
  Phone,
  Grid3X3,
  MoreVertical,
  DollarSign,
  Briefcase,
  Activity,
  BarChart3,
  Wrench,
  Globe,
  CreditCard,
  LogOut,
  Bot,
  User,
  CheckSquare,
  Settings2,
  Clock,
  Megaphone,
  GlobeIcon,
  MapPin,
  BarChart,
  TrendingUp,
  LineChart,
  MessageSquare,
  ListChecks,
  HelpCircle,
  LayoutDashboard,
  ArrowLeft
} from 'lucide-react';
import Services from './pages/Services';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import Stock from './pages/Stock';
import SellProducts from './pages/SellProducts';
import OnlineStore from './pages/OnlineStore';
import Invoices from './pages/Invoices';
import Payroll from './pages/Payroll';
import ClockInPincode from './pages/ClockInPincode';
import GiftCards from './pages/GiftCards';
import InventoryMaterials from './pages/inventory/Materials';
import AppStore from './pages/AppStore';
import MyApps from './pages/MyApps';
import Websites from './pages/Websites';
import Tutorial from './pages/Tutorial';
import Home from './pages/Home';
import ErrorBoundary from './components/ErrorBoundary';
import AppRouteGuard from './components/AppRouteGuard';
import SubscriptionGuard from './components/SubscriptionGuard';
import SetupGuard from './components/SetupGuard';
import HomeScreenRedirect from './components/HomeScreenRedirect';
import Proposals from './pages/Proposals';
import Projects from './pages/Projects';
import Documents from './pages/Documents';
import Expenses from './pages/Expenses';
import Subscriptions from './pages/Subscriptions';
import AIInsights from './pages/AIInsights';
import Resources from './pages/Resources';
import TeamsResourcePermissions from './pages/teams/ResourcePermissions';
import MobileCalendarOnlineBooking from './pages/mobile-calendar/OnlineBooking';
import Payments from './pages/Payments';
import Workflows from './pages/Workflows';
import Que from './pages/Que';
import ManagePayroll from './pages/ManagePayroll';
import Settings from './pages/Settings';
import BusinessSettings from './pages/BusinessSettings';
import AISettings from './pages/AISettings';
import AgentsPage from './pages/Agents';
import Marketing from './pages/Marketing';
import FlatRates from './pages/settings/FlatRates';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import SignupNewBusiness from './pages/auth/SignupNewBusiness';
import SignupNexus from './pages/auth/SignupNexus';
import SignupNexusEmail from './pages/auth/SignupNexusEmail';
import SignupNexusPreparing from './pages/auth/SignupNexusPreparing';
import Setup from './pages/auth/Setup';
import ResetPassword from './pages/auth/ResetPassword';
import UpdatePassword from './pages/auth/UpdatePassword';
import AccountDisabled from './pages/auth/AccountDisabled';
import AdminSubscriptionManagement from './pages/AdminSubscriptionManagement';
import ResourceSubscriptionExpired from './components/ResourceSubscriptionExpired';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import TeamReports from './pages/TeamReports';
import GPS from './pages/GPS';
import PrivateRoute from './components/PrivateRoute';
import AuthGuard from './components/AuthGuard';
import { supabase } from './lib/supabaseClient';
import { BusinessProvider, useBusiness } from './contexts/BusinessContext';
import { ImageViewerProvider } from './components/ImageViewerProvider';
import { LocationTrackingDebugProvider } from './contexts/LocationTrackingDebugContext';
// import { UserProvider } from './contexts/UserContext'; // Temporarily disabled
import { DataCacheProvider } from './contexts/DataCacheContext';
import Profile from './pages/Profile';
import { getResourceByAuthId } from './lib/resources';
import { setupInspectionPhotosBucket } from './lib/storage';
import { SupabaseProvider } from './contexts/SupabaseContext';
import { SimpleTimerProvider } from './contexts/SimpleTimerContext';
import { MouseEvent as DOMMouseEvent } from 'react';
import SalesReports from './pages/SalesReports';
import Sales from './pages/Sales';
import ProfitLoss from './pages/ProfitLoss';
import LeftNavigationV2 from './components/LeftNavigationV2';
import PublicReceipt from './pages/PublicReceipt';
import PublicPDFReceipt from './pages/PublicPDFReceipt';
import PublicProposal from './pages/PublicProposal';
import PublicIntakeForm from './pages/PublicIntakeForm';
import FloatingActionButton from './components/FloatingActionButton';
import FloatingVoicePill from './components/agents/FloatingVoicePill';
import { authService } from './services/authService';
import { navigationService } from './services/navigationService';

import { NotificationsProvider } from './contexts/NotificationsContext';
// UnreadTasksBadge import removed
// import CashFlow from './pages/CashFlow'; // Disabled
import BookingStatus from './contexts/BookingStatusContext';
import BookOnline from './pages/public/BookOnline';
import BuyGiftCard from './pages/public/BuyGiftCard';
import BookingSuccess from './pages/public/BookingSuccess';
import GiftCardSuccess from './pages/public/GiftCardSuccess';
import PublicLeadForm from './pages/public/PublicLeadForm';
import PayInvoice from './pages/public/PayInvoice';
import PayInvoiceSuccess from './pages/public/PayInvoiceSuccess';
import StoreOnline from './pages/public/StoreOnline';
import MeetBook from './pages/public/MeetBook';
import CustomerPortal from './pages/public/CustomerPortal';
import PublicQue from './pages/public/PublicQue';
import PublicQueueStatus from './pages/public/PublicQueueStatus';
import QueueThankYou from './pages/public/QueueThankYou';
import ActivateSubscription from './pages/public/ActivateSubscription';
import Messages from "./pages/Messages";
import Automations from "./pages/Automations"; // Keeping for fallback - disabled in routing
import AutomationsV2 from "./pages/AutomationsV2";
import AIAgents from "./pages/AIAgents";
import ReceptionistBuilderPage from "./pages/agents/ReceptionistBuilderPage";
import AIAgentBuilder from "./pages/AIAgentBuilder";
import OnlineBookings from './pages/marketing/OnlineBookings';
import OnlineBookingEditor from './pages/OnlineBookingEditor';
import { ToastProvider } from './components/toasts/SimpleToast';
import { NotificationsDropdown } from './components/NotificationsDropdown';
import BusinessProfileDropdown from './components/BusinessProfileDropdown';
import HelpModal from './components/HelpModal';
import NexusVoiceIntroModal from './components/NexusVoiceIntroModal';
import AssistantHighlightOverlay from './components/AssistantHighlightOverlay';
import AssistantGhostMatches from './components/AssistantGhostMatches';
import { TeamMapModalHost } from './components/admin-dashboard/TeamMapModal';
import { VoiceHelpProvider } from './contexts/VoiceHelpContext';
import { AgentBetaProvider } from './contexts/AgentBetaContext';
import { useNexusAiOnboarding } from './hooks/useNexusAiOnboarding';
import AISetupAssistantModal from './components/AISetupAssistantModal';
import DomainSetupHelperModal from './components/DomainSetupHelperModal';
import AutoTopupFailedModal from './components/AutoTopupFailedModal';
import AppUpdateModal from './components/AppUpdateModal';

import CreateBookingModal from './components/CreateBookingModal';
import BookingHelperModal from './components/BookingHelperModal';
import { useBookingHelper } from './hooks/useBookingHelper';
import CreateProposalModal from './components/CreateProposalModal';
import WithdrawStockModal from './components/WithdrawStockModal';
import PaymentModal from './components/PaymentModal';
import { migrateLocalStorageToDatabase } from './utils/migrations';
import { useReminderProcessor } from './hooks/useReminderProcessor';

import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { UnreadOpportunitiesProvider } from './contexts/UnreadOpportunitiesContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { CallProvider } from './contexts/CallContext';
import { ApplicationSettingsProvider, useApplicationSettingsContext } from './contexts/ApplicationSettingsContext';
import { useSelectInputOnFocus } from './lib/utils';
import { PageId } from './hooks/useApplicationSettings';
import CalendarV2 from './pages/CalendarV2';
import MobileCalendar from './pages/MobileCalendar';

import SuppliesUsage from './pages/SuppliesUsage';
import CRM from './pages/CRM';
import CrmLeadFormsPage from './pages/crm/LeadForms';
import LeadFormBuilderPage from './pages/crm/lead-forms/LeadFormBuilderPage';
import CreateMobileBookingModal from './components/CreateMobileBookingModal';
import TestNotification from './pages/TestNotification';
import ToastContainer from './components/ToastContainer';
import Dialler from './pages/Dialler';
import CallsBeta from './pages/CallsBeta';
import { DiallerDropdown } from './components/DiallerDropdown';
import { DeepLinkNavigator } from './components/DeepLinkNavigator';
import TasksAssistantHost from './components/admin-dashboard/TasksAssistantHost';
import GoogleBusinessOauthReturnListener from './components/GoogleBusinessOauthReturnListener';
import OauthAppReturn from './pages/OauthAppReturn';
import { OrdersDropdown } from './components/OrdersDropdown';
import { BackgroundJobsDropdown } from './components/BackgroundJobsDropdown';
import ControlCentreTasksBadge from './components/admin-dashboard/ControlCentreTasksBadge';
import ActiveCallModal from './components/ActiveCallModal';
import FeedbackPage from './pages/FeedbackPage';
import ReferralPartnerInvite from './pages/ReferralPartnerInvite';
import LiabilityWaiver from './pages/public/LiabilityWaiver';
import ResourceInviteAccept from './pages/public/ResourceInviteAccept';
import BookingInfoModal from './components/BookingInfoModal';
import KPIDashboard from './pages/KPIDashboard';
import AdminDashboard from './pages/AdminDashboard';
import { PermissionProvider } from './contexts/PermissionContext';
import PermissionGuard from './components/PermissionGuard';
import { usePermissionCheck } from './hooks/usePermissionCheck';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { mobileService } from './lib/mobile';
import { authStateManager, AuthStateManager } from './lib/authStateManager';
import { sessionBackupManager } from './lib/sessionBackupManager';
import { sessionMonitor } from './lib/sessionMonitor';
// Removed all persistence imports
// Removed SessionStatusIndicator
import RexTesting from './pages/RexTesting';
import Website from './website/Website';
import RexTraining from './pages/RexTraining';
import { useAppSystem } from './hooks/useAppSystem';
import { performGlobalSearch, SearchResult, SearchResults } from './services/globalSearchService';
import GlobalSearchDropdown from './components/GlobalSearchDropdown';
import SubscriptionPastDueBanner from './components/SubscriptionPastDueBanner';
import { stripeSyncedPastDueShowsBillingBanner } from './lib/subscription';


// Development flag to control feature visibility
const isDevelopment = process.env.NODE_ENV === "development";

/** Old standalone integrations page. Nexus Connect is the only integrations screen. */
function LegacyIntegrationsRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (!params.get('tab')) params.set('tab', 'integrations');
  return <Navigate to={`/app-store?${params.toString()}`} replace />;
}

const AppContent = memo(function AppContent() {
  const location = useLocation();

  // Credit AI tokens after Stripe Checkout return (?ai_tokens_checkout=1&session_id=…)
  useAiTokenCheckoutReturn();
  // Capture email campaign payment after Stripe Checkout return
  useEmailCampaignCheckoutReturn();
  
  // Get business context FIRST - needed for activity logging
  const { selectedBusiness, loading: businessLoading, error: businessError, forceLoadBusinesses } = useBusiness();

  // Remote session monitoring state (for end-session button in header)
  const remoteSession = useRemoteSessionSafe();

  // Current Bookings Helper — admin-only modal flagging out-of-sync bookings on load.
  const bookingHelper = useBookingHelper();

  // Initialize sidebar state - always closed by default
  const initialIsMobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  /** True while the mobile bottom bar is showing — it hosts the apps menu, so
   *  the header hamburger stands down and the menu opens upward instead. */
  const [bottomBarActive, setBottomBarActive] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [unviewedMissedCount, setUnviewedMissedCount] = useState(0);
  const [pinOrdersWidget, setPinOrdersWidget] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  
  // Global search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults>({
    customers: [],
    bookings: [],
    invoices: [],
    proposals: [],
    services: [],
    products: [],
    resources: [],
    pages: [],
    settings: [],
    tasks: [],
    opportunities: [],
    giftcards: [],
    discountcodes: [],
    campaigns: [],
    templates: [],
    leadforms: [],
  });
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get installed apps for filtering search results
  const { coreApps, installedAddons } = useAppSystem();
  const installedAppSlugs = new Set([
    ...(coreApps || []).map(app => app.app?.slug).filter(Boolean),
    ...(installedAddons || []).map(app => app.app?.slug).filter(Boolean),
  ]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Reset search state when location changes (navigation happens)
  useEffect(() => {
    setIsSearchExpanded(false);
    setIsSearchDropdownOpen(false);
    setSearchQuery('');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, [location.pathname]);

  // Load pin orders widget setting
  useEffect(() => {
    const loadPinOrdersSetting = async () => {
      if (!selectedBusiness?.id) return;
      try {
        const { data, error } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'inventory',
          p_setting_key: 'pin_orders_widget',
          p_default_value: false
        });
        if (!error && data !== null) {
          setPinOrdersWidget(data);
        }
      } catch (err) {
        console.warn('Failed to load pin orders widget setting:', err);
      }
    };
    loadPinOrdersSetting();

    const handleSettingChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === 'pin_orders_widget') {
        setPinOrdersWidget(detail.value);
      }
    };
    window.addEventListener('inventorySettingChanged', handleSettingChange);
    return () => window.removeEventListener('inventorySettingChanged', handleSettingChange);
  }, [selectedBusiness?.id]);

  // Activity logging: Track app startup
  useEffect(() => {
    const logAppStartup = async () => {
      try {
        const { logAppStart } = await import('./services/activityLogService');
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await logAppStart(user.id, selectedBusiness?.id);
        }
      } catch (error) {
        // Silently fail - activity logging should never break the app
        console.warn('Failed to log app startup:', error);
      }
    };
    logAppStartup();

    // Native App Store review: count the session, then (well after launch, so
    // it never collides with startup UI) ask if the user has earned the prompt.
    import('./lib/appReview').then(({ trackAppSessionForReview, maybeRequestAppReview }) => {
      trackAppSessionForReview();
      setTimeout(() => void maybeRequestAppReview('app_open'), 25_000);
    }).catch(() => { /* non-critical */ });
  }, []); // Only run once on mount

  // AI token auto top-up: opportunistic check on app open (server no-ops
  // unless the balance is under the configured threshold).
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const timer = setTimeout(() => {
      import('./lib/aiTokenAutoTopup')
        .then(({ runAutoTopupCheck }) => void runAutoTopupCheck(selectedBusiness.id))
        .catch(() => { /* non-critical */ });
    }, 8_000);
    return () => clearTimeout(timer);
  }, [selectedBusiness?.id]);

  // Activity logging: Track page visits
  useEffect(() => {
    const logPageVisit = async () => {
      // Skip logging for public routes and auth pages
      const publicRoutes = ['/login', '/signup', '/sign-up-new-business', '/Signup-nexus', '/signup-nexus', '/setup', '/reset-password', '/account-disabled'];
      const isPublicRoute = publicRoutes.includes(location.pathname) ||
                           location.pathname.startsWith('/signup-nexus/') ||
                           location.pathname.startsWith('/Signup-nexus/') ||
                           location.pathname.startsWith('/:') ||
                           location.pathname.includes('/book') ||
                           location.pathname.includes('/meet/') ||
                           location.pathname.includes('/proposal/') ||
                           location.pathname.includes('/intake/') ||
                           location.pathname.includes('/receipt/') ||
                           location.pathname.includes('/pay-invoice/') ||
                           location.pathname.includes('/referral-partner/');
      
      if (isPublicRoute) return;

      try {
        const { logPageVisit } = await import('./services/activityLogService');
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Map path to page name
          const pageNameMap: Record<string, string> = {
            '/': 'Home',
            '/calendarV2': 'Calendar',
            '/bookings': 'Bookings',
            '/customers': 'Customers',
            '/services': 'Services',
            '/invoices': 'Invoices',
            '/proposals': 'Proposals',
            '/resources': 'Resources',
            '/crm': 'CRM',
            '/marketing': 'Marketing',
            '/documents': 'Docs',
            '/messages': 'Messages',
            '/automations': 'Automate',
            '/stock': 'Inventory',
            '/sell-products': 'Sell Products',
            '/online-store': 'Online Store',
            '/app-store': 'Nexus Connect',
            '/my-apps': 'My Apps',
            '/business-settings': 'Business Settings',
            '/ai-settings': 'AI Settings',
            '/agents': 'Agents',
            '/super-admin': 'Super Admin',
            '/kpi-dashboard': 'KPI Dashboard',
            '/admin-dashboard': 'Control Centre',
          };

          // Check for settings routes
          let pageName = pageNameMap[location.pathname];
          if (!pageName) {
            if (location.pathname.startsWith('/settings')) {
              pageName = 'Settings';
            } else if (location.pathname.startsWith('/mobile-calendar')) {
              pageName = 'Field-Pro';
            } else if (location.pathname.startsWith('/inventory')) {
              pageName = 'Inventory';
            } else {
              pageName = location.pathname.split('/').pop() || 'Unknown Page';
            }
          }

          // Determine category based on path
          let category: 'navigation' | 'booking' | 'customer' | 'invoice' | 'proposal' | 'product' | 'service' | 'resource' | 'team' | 'crm' | 'marketing' | 'settings' | 'system' = 'navigation';
          if (location.pathname.includes('/booking') || location.pathname.includes('/calendar')) {
            category = 'booking';
          } else if (location.pathname.includes('/customer')) {
            category = 'customer';
          } else if (location.pathname.includes('/invoice')) {
            category = 'invoice';
          } else if (location.pathname.includes('/proposal')) {
            category = 'proposal';
          } else if (location.pathname.includes('/product') || location.pathname.includes('/stock') || location.pathname.includes('/store')) {
            category = 'product';
          } else if (location.pathname.includes('/service')) {
            category = 'service';
          } else if (location.pathname.includes('/resource') || location.pathname.includes('/team')) {
            category = 'resource';
          } else if (location.pathname.includes('/crm')) {
            category = 'crm';
          } else if (location.pathname.includes('/marketing')) {
            category = 'marketing';
          } else if (location.pathname.includes('/setting')) {
            category = 'settings';
          }

          await logPageVisit(location.pathname, pageName, category, user.id, selectedBusiness?.id);
        }
      } catch (error) {
        // Silently fail - activity logging should never break the app
        console.warn('Failed to log page visit:', error);
      }
    };
    logPageVisit();
  }, [location.pathname, selectedBusiness?.id]);
  const appContentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [hasBookings, setHasBookings] = useState(false);
  const [hasCalendar, setHasCalendar] = useState(false);

  const [proposalsAccess, setProposalsAccess] = useState<
    "manage" | "view" | "disabled"
  >("disabled");
  const [hasPayments, setHasPayments] = useState(false);
  const [hasCustomers, setHasCustomers] = useState(false);
  const [hasPayroll, setHasPayroll] = useState(false);

  const [hasSettings, setHasSettings] = useState(false);

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [userInfo, setUserInfo] = useState<{ avatarUrl?: string } | null>(null);
  
  // Nexus AI Assistant state
  const [isNexusOverlayOpen, setIsNexusOverlayOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [bookingsPermissionLevel, setBookingsPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");

  const [settingsPermissionLevel, setSettingsPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");
  // Task-related hooks removed
  const [hasMessages, setHasMessages] = useState(false);
  // Get the application settings context to check for disabled pages
  const { isPageDisabled } = useApplicationSettingsContext();
  
  // Get permission checking functionality
  const { canAccess, isLoading: permissionsLoading, isAdmin, canViewAdminDashboard } = usePermissionCheck();
  
  // Track if initial load is complete to prevent sidebar flash during startup
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // Mark initial load as complete once business and permissions are loaded
  // Reduced delay for faster startup - iOS apps feel instant
  useEffect(() => {
    if (!businessLoading && !permissionsLoading && selectedBusiness) {
      // Minimal delay - just enough to prevent flash, not block rendering
      const timer = setTimeout(() => {
        setIsInitialLoadComplete(true);
      }, 100); // Reduced from 500ms to 100ms for faster startup
      return () => clearTimeout(timer);
    } else {
      // Reset if loading starts again
      setIsInitialLoadComplete(false);
    }
  }, [businessLoading, permissionsLoading, selectedBusiness]);

  // Refetch business when tab becomes visible so DB changes (e.g. cron / webhooks) show the past_due banner
  // without a full reload. Previously we only refetched when already past_due, which missed the transition.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      forceLoadBusinesses({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [forceLoadBusinesses]);

  // While the shell is open, poll occasionally so subscription_status updates even if the tab stays focused.
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        forceLoadBusinesses({ silent: true });
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [selectedBusiness?.id, forceLoadBusinesses]);
  
  // FAB-related modal states - moved up for access by MobileBottomNavigation
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [isCreateWarrantyModalOpen, setIsCreateWarrantyModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const {
    termsAccepted: aiTermsAccepted,
    welcomeClaimed: aiWelcomeClaimed,
    showIntro: showAiIntro,
    setShowIntro: setShowAiIntro,
    setPendingAction: setAiPendingAction,
    requestAccess: requestAiAccess,
    markComplete: markAiOnboardingComplete,
  } = useNexusAiOnboarding();
  const [isAISetupAssistantOpen, setIsAISetupAssistantOpen] = useState(false);
  /** Deep-link target when an OAuth redirect returns mid-setup-wizard (e.g. Gmail connect). */
  const [aiSetupResumeTarget, setAiSetupResumeTarget] = useState<{ categoryId: string; itemId: string } | null>(null);
  // OAuth return handling for the AI Setup Assistant. Connect flows (e.g.
  // Gmail) send the browser through an external OAuth page and redirect back
  // with `aiSetup=<categoryId>.<itemId>` in the URL. Two cases:
  //  - We're the OAuth popup: notify the window that opened us and close.
  //  - The OAuth ran in this tab (popup blocked / PWA): reopen the wizard on
  //    the exact step the user was on so the flow resumes seamlessly.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawTarget = params.get('aiSetup');
    if (!rawTarget) return;

    const oauthSuccess = window.location.search.includes('success=gmail_connected');
    if (window.opener && oauthSuccess) {
      try {
        window.opener.postMessage({ type: 'GMAIL_OAUTH_SUCCESS' }, window.location.origin);
      } catch {
        // Opener may be gone; the poller in the opener also detects success.
      }
      window.close();
      return;
    }

    // Tolerate a malformed `?a=b?c=d` join from the OAuth callback redirect.
    const [categoryId, itemId] = rawTarget.split('?')[0].split('.');
    if (categoryId && itemId) {
      setAiSetupResumeTarget({ categoryId, itemId });
      setIsAISetupAssistantOpen(true);
    }

    // Strip the one-shot OAuth params so refreshes don't re-trigger this.
    const url = new URL(window.location.href);
    url.searchParams.delete('aiSetup');
    url.searchParams.delete('success');
    url.searchParams.delete('twilio_connect');
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Helper function to check if a page should be shown based on permissions and disabled status
  const shouldShowPage = (pageId: PageId, hasPermission: boolean) => {
    return hasPermission && !isPageDisabled(pageId);
  };

  // PWA detection effect
  useEffect(() => {
    // Method 1: Check for display-mode: standalone
    const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 2: Check for iOS PWA (using window.navigator)
    const isIOSPWA = 
      // @ts-ignore - 'standalone' exists on iOS Safari but not in the TypeScript definition
      (window.navigator as any).standalone || 
      window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 3: Check for PWA launch parameters
    const urlParams = new URLSearchParams(window.location.search);
    const launchedAsPWA = urlParams.get('pwa') === 'true';
    
    // Method 4: Check for iOS specific user agent
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    
    // Force PWA mode for testing if needed
    // const isPWALocal = true; // Uncomment for testing
    const isPWALocal = isDisplayModeStandalone || isIOSPWA || launchedAsPWA;

    // Detect Capacitor native app (iOS/Android) to tune viewport and body classes
    import('@capacitor/core').then(({ Capacitor }) => {
      const native = Capacitor.isNativePlatform();
      if (native) {
        document.body.classList.add('native-app');
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        if (viewportMeta) {
          viewportMeta.setAttribute('content',
            'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no');
        }
      }
    }).catch(() => {});
    
    // Add PWA class to body if in PWA mode
    if (isPWALocal) {
      document.body.classList.add('pwa-mode');
      
      // Set URL parameter for any page loads - ONLY if not already set and not already launched as PWA
      if (!launchedAsPWA && !urlParams.has('pwa')) {
        try {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('pwa', 'true');
          // Use replaceState instead of pushState to avoid adding to history stack
          window.history.replaceState({path: newUrl.toString()}, '', newUrl.toString());
        } catch (error) {
          console.warn('Failed to update URL for PWA mode:', error);
        }
      }
      
      // Apply iOS-specific classes if needed
      if (isIOS) {
        document.body.classList.add('ios-pwa-mode');
        // Fix for iOS bottom nav positioning
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        if (viewportMeta) {
          viewportMeta.setAttribute('content', 
            'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
        } else {
          // Create viewport meta if it doesn't exist
          const meta = document.createElement('meta');
          meta.name = 'viewport';
          meta.content = 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no';
          document.head.appendChild(meta);
        }
      }
      
      console.log('Running in PWA mode', { isIOS, isDisplayModeStandalone, isIOSPWA });
    } else {
      document.body.classList.remove('pwa-mode');
      document.body.classList.remove('ios-pwa-mode');
      console.log('Running in browser mode');
    }
  }, []); // Empty dependency array to run only once on mount

  // Open Nexus AI Set Up Assistant when user lands after completing setup (Get Started) — admin only
  useEffect(() => {
    if (!isAdmin) return;
    const authOrSetupPaths = ['/login', '/signup', '/sign-up-new-business', '/Signup-nexus', '/signup-nexus', '/setup', '/admin-subscription-management', '/reset-password', '/update-password', '/account-disabled', '/subscription-success'];
    const isMainApp = !authOrSetupPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (isMainApp && typeof sessionStorage !== 'undefined' && sessionStorage.getItem('openAISetupAssistant') === 'true') {
      sessionStorage.removeItem('openAISetupAssistant');
      setIsAISetupAssistantOpen(true);
    }
  }, [location.pathname, isAdmin]);

  // Only show loading screen if we don't have a business selected yet
  // This prevents unmounting when businessLoading changes during tab switches
  if (businessLoading && !selectedBusiness) {
    return <BasicLoadingScreen />;
  }

  // Show error screen if business context failed to load
  if (businessError && !selectedBusiness && !businessLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Connection Error
            </h2>
            <p className="text-red-600 mb-4">
              {businessError}
            </p>
            <button
              onClick={() => {
                console.log('🔄 User clicked retry - forcing business reload');
                forceLoadBusinesses();
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine if we're still in initial loading phase
  // Hide sidebar during any loading state or before initial load completes
  const isStillLoading = businessLoading || permissionsLoading || !isInitialLoadComplete;

  const showPastDueBanner =
    location.pathname !== '/payroll/pincode' &&
    stripeSyncedPastDueShowsBillingBanner(
      selectedBusiness?.subscription_status,
      selectedBusiness?.subscription_past_due_at,
    );
  const pastDueBannerHeight = showPastDueBanner ? '2.75rem' : '0px';
  const mainContentTopMargin =
    location.pathname === '/payroll/pincode'
      ? '0'
      : `calc(env(safe-area-inset-top, 0px) + 3rem + ${pastDueBannerHeight})`;

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--control-centre-activity-rail-top',
      mainContentTopMargin === '0'
        ? '0px'
        : mainContentTopMargin,
    );
    return () => {
      document.documentElement.style.removeProperty('--control-centre-activity-rail-top');
    };
  }, [mainContentTopMargin]);

  const pastDueBannerPortal =
    showPastDueBanner && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed left-0 right-0 z-[200] pointer-events-auto shadow-md"
            style={{ top: 'env(safe-area-inset-top, 0px)' }}
          >
            <SubscriptionPastDueBanner subscriptionPastDueAt={selectedBusiness?.subscription_past_due_at} />
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className="min-h-screen bg-gray-100 relative overflow-x-hidden"
      ref={appContentRef}
      data-app-content
    >
      {pastDueBannerPortal}
      {location.pathname !== '/payroll/pincode' && (
      <>
      {/* Black cover for the iOS safe area / notch space above the nav bar */}
      <div
        className="fixed top-0 left-0 right-0 z-[80] bg-black"
        style={{ height: 'env(safe-area-inset-top, 0px)' }}
      />
      <div
        className="fixed left-0 right-0 w-full z-[80] shadow-lg shadow-black/20 transition-all duration-300 ease-out"
        style={{
          touchAction: 'none',
          WebkitOverflowScrolling: 'auto',
          top: showPastDueBanner
            ? `calc(env(safe-area-inset-top, 0px) + ${pastDueBannerHeight})`
            : 'env(safe-area-inset-top, 0px)',
          transform: 'translateZ(0)',
          willChange: 'transform',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
        }}
      >
        <header className="main-header bg-black backdrop-blur-xl bg-opacity-95 border-b border-white/20 px-4 h-12 flex shrink-0 items-center justify-between w-full">
        <div className="flex items-center space-x-1 md:space-x-4">
          {!bottomBarActive && (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
            aria-label="Toggle sidebar menu"
            data-assistant-anchor="menu-button"
          >
            <div className="flex flex-col space-y-1">
              <div className={`w-5 h-0.5 bg-white transition-all duration-300 ${sidebarOpen ? 'rotate-45 translate-y-1.5' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-white transition-all duration-300 ${sidebarOpen ? 'opacity-0' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-white transition-all duration-300 ${sidebarOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></div>
            </div>
          </button>
          )}

          {ImpersonationService.isImpersonating() && (
            <button
              onClick={() => ImpersonationService.stopImpersonation()}
              className="flex items-center gap-1.5 px-2 py-1 text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
              aria-label="Exit impersonation"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium truncate max-w-[140px]">
                {ImpersonationService.getImpersonationData()?.targetBusinessName || 'Business'}
              </span>
            </button>
          )}

          <nav className="hidden md:flex items-center space-x-6">
          </nav>
        </div>

        <div className="flex items-center space-x-2">
          {/* Remote session end button — shown when screen is being monitored */}
          {remoteSession?.isBeingMonitored && (
            <button
              onClick={() => remoteSession.endSession?.()}
              className="remote-session-block flex items-center gap-1.5 px-2.5 py-1 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span className="text-white text-xs font-medium">End</span>
            </button>
          )}

          {/* Pin Orders Widget */}
          {pinOrdersWidget && <OrdersDropdown />}

          {/* Background jobs (bulk AI Tasks, etc.) */}
          <BackgroundJobsDropdown />

          {/* Control Centre Tasks */}
          {selectedBusiness ? <ControlCentreTasksBadge variant="nav" /> : null}

          {/* Home / Control Centre — on the native app this moves to the FAB stack */}
          {selectedBusiness && !Capacitor.isNativePlatform() && (
            <Link
              to="/admin-dashboard"
              className={`p-2 rounded-lg transition-colors touch-manipulation ${
                location.pathname === '/admin-dashboard'
                  ? 'text-white bg-gray-800/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
              aria-label={canViewAdminDashboard ? 'Control Centre' : 'Home'}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              data-assistant-anchor="control-centre-button"
            >
              <LayoutDashboard className="h-5 w-5" />
            </Link>
          )}

          {/* Dial Pad Dropdown */}
          {canAccess.messages && <DiallerDropdown unviewedMissedCount={unviewedMissedCount} />}

          {/* Notifications Dropdown — placeholder with no real data yet; dev-only
              until notifications are actually wired up. */}
          {import.meta.env.DEV && <NotificationsDropdown />}

          {/* Help - How do I...? (gated by AI onboarding: intro → free tokens → terms) */}
          {/* AI Help is admin-only — resources see it only when granted admin */}
          {canViewAdminDashboard ? (
            <button
              type="button"
              onClick={() => {
                if (!requestAiAccess('help')) return;
                setIsHelpModalOpen(true);
              }}
              className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
              aria-label="Help - How do I..."
              data-assistant-anchor="help-button"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          ) : null}
          
          {/* Business Profile Dropdown */}
          <BusinessProfileDropdown />
        </div>
      </header>
      </div>
      </>
      )}

      <div
        className="flex min-h-[calc(100vh-3rem)]"
        style={{
          marginTop: mainContentTopMargin,
          // Keeps page content above the mobile bottom bar (0px when hidden).
          paddingBottom: 'var(--bottom-nav-height, 0px)',
        }}
      >
        {/* Backdrop overlay for mobile - only show for non-PWA mode since PWA uses CSS backdrop */}
        {/* Only render backdrop after initial load is complete and not loading */}
        {!isStillLoading && isInitialLoadComplete && sidebarOpen && isMobile && !document.body.classList.contains('pwa-mode') && (
          <div
            className="fixed inset-0 bg-transparent z-[75] transition-opacity duration-300 ease-in-out"
            onClick={() => setSidebarOpen(false)}
            onTouchMove={(e) => e.preventDefault()}
            onWheel={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            style={{ 
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              pointerEvents: 'auto',
              overscrollBehavior: 'none',
              left: isMobile ? '0' : '280px',
              width: isMobile ? '100%' : 'calc(100% - 280px)'
            }}
            aria-hidden="true"
          />
        )}

        {/* Backdrop overlay - subtle dim without blur */}
        {/* Only render backdrop after initial load is complete and not loading */}
        {!isStillLoading && isInitialLoadComplete && (
        <div
          className={`fixed inset-0 z-[89] transition-opacity duration-300 ease-out ${
            sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{
            background: sidebarOpen 
              ? 'rgba(0, 0, 0, 0.3)' 
              : 'transparent',
          }}
          onClick={() => setSidebarOpen(false)}
        />
        )}

        {/* Navigation Dropdown - iOS-style with spring animations */}
        {/* Only render sidebar after initial load is complete and not loading to prevent flash during login */}
        {!isStillLoading && isInitialLoadComplete && location.pathname !== '/payroll/pincode' ? (
        <div
          data-app-dropdown-menu
            data-ready={isInitialLoadComplete ? "true" : "false"}
          className={`fixed left-0 ${isMobile ? 'w-full' : 'w-[220px]'} bg-black ${
            bottomBarActive ? 'rounded-t-2xl' : isMobile ? 'rounded-b-none' : 'rounded-b-2xl'
          } z-[90] overflow-y-auto ios-momentum-scroll ${
            sidebarOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
          style={{
            // With the mobile bottom bar the menu is anchored to the bar and
            // grows upward; otherwise it drops down from the header as before.
            ...(bottomBarActive
              ? {
                  bottom: 'var(--bottom-nav-height, 0px)',
                  paddingTop: '0.75rem',
                  // Sized to its content, capped so it never becomes full-screen
                  // (expanding Hidden Apps just grows it further up).
                  maxHeight:
                    'calc(100vh - env(safe-area-inset-top, 0px) - 3rem - var(--bottom-nav-height, 0px) - 1rem)',
                  transform: sidebarOpen ? 'translateY(0) scale(1)' : 'translateY(100%) scale(0.98)',
                  transformOrigin: 'bottom center',
                  animation: sidebarOpen
                    ? 'iosSlideUpIn 0.45s cubic-bezier(0.34, 1.2, 0.64, 1) forwards'
                    : undefined,
                  boxShadow: sidebarOpen ? '0 -20px 60px rgba(0, 0, 0, 0.5)' : 'none',
                }
              : {
                  top: 'env(safe-area-inset-top, 0px)',
                  paddingTop: `calc(3rem + ${pastDueBannerHeight})`,
                  maxHeight: 'calc(100vh - env(safe-area-inset-top, 0px) - 60px)',
                  transform: sidebarOpen ? 'translateY(0) scale(1)' : 'translateY(-100%) scale(0.95)',
                  transformOrigin: 'top center',
                  animation: sidebarOpen
                    ? 'iosSlideDown 0.45s cubic-bezier(0.34, 1.2, 0.64, 1) forwards'
                    : undefined,
                  boxShadow: sidebarOpen ? '0 20px 60px rgba(0, 0, 0, 0.5)' : 'none',
                }),
            opacity: sidebarOpen ? 1 : 0,
            willChange: sidebarOpen ? 'transform, opacity' : undefined,
            // Ensure it's completely hidden when closed
            display: sidebarOpen ? 'block' : 'none',
          }}
        >
          <LeftNavigationV2 
            isOpen={sidebarOpen}
            onAppClick={() => {
              // Close the dropdown menu when an app is clicked
              setSidebarOpen(false);
            }}
          />
        </div>
        ) : null}

        {/* Main content area - no longer needs to resize for dropdown */}
        <div className={`flex-1 transition-all duration-300 opacity-100 w-full max-w-full`}
          style={{
            pointerEvents: isMobile && sidebarOpen ? 'none' : 'auto',
            // Removed width and marginLeft adjustments since dropdown doesn't take up horizontal space
          }}
        >
          <main 
            className={`main-content ${location.pathname === '/crm' || location.pathname === '/customers' || location.pathname === '/services' || location.pathname === '/invoices' || location.pathname === '/expenses' || location.pathname === '/payroll' ? 'bg-white' : 'bg-gray-100'} ${location.pathname !== '/tasks' && location.pathname !== '/calendarV2' && location.pathname !== '/mobile-calendar' && location.pathname !== '/messages' && location.pathname !== '/marketing' && location.pathname !== '/kpi-dashboard' && location.pathname !== '/admin-dashboard' && location.pathname !== '/crm' && location.pathname !== '/customers' && location.pathname !== '/services' && location.pathname !== '/proposals' && location.pathname !== '/bookings' && location.pathname !== '/resources' && location.pathname !== '/automations' && location.pathname !== '/stock' && location.pathname !== '/invoices' && location.pathname !== '/expenses' && location.pathname !== '/payroll' && location.pathname !== '/sell-products' && location.pathname !== '/online-store' && location.pathname !== '/app-store' && location.pathname !== '/my-apps' && location.pathname !== '/super-admin' && !location.pathname.startsWith('/mobile-calendar/') && !location.pathname.startsWith('/inventory/') && !location.pathname.startsWith('/settings') && !location.pathname.startsWith('/billing') && location.pathname !== '/integrations' && location.pathname !== '/business-settings' && location.pathname !== '/agents' && !location.pathname.startsWith('/crm/lead-forms') ? 'p-4 md:p-6' : 'p-0'} ${isMobile && location.pathname !== '/marketing' && location.pathname !== '/kpi-dashboard' && location.pathname !== '/messages' && location.pathname !== '/crm' && location.pathname !== '/customers' && location.pathname !== '/services' && location.pathname !== '/proposals' && location.pathname !== '/bookings' && location.pathname !== '/resources' && location.pathname !== '/automations' && location.pathname !== '/stock' && location.pathname !== '/sell-products' && location.pathname !== '/online-store' && location.pathname !== '/super-admin' && !location.pathname.startsWith('/settings') && !location.pathname.startsWith('/billing') && location.pathname !== '/integrations' && location.pathname !== '/business-settings' && location.pathname !== '/agents' && !location.pathname.startsWith('/crm/lead-forms') ? 'pb-4' : ''} ${location.pathname === '/calendarV2' ? 'calendar-v2-page' : ''} ${location.pathname === '/proposals' ? 'proposals-page' : ''} ${location.pathname === '/bookings' ? 'bookings-page' : ''} ${location.pathname === '/resources' ? 'resources-page' : ''} ${location.pathname === '/services' ? 'services-page' : ''} ${location.pathname === '/crm' ? 'crm-page' : ''} ${location.pathname === '/customers' ? 'customers-page' : ''} ${location.pathname === '/kpi-dashboard' ? 'kpi-dashboard-page' : ''} ${location.pathname === '/admin-dashboard' ? 'admin-dashboard-page' : ''} ${location.pathname === '/tasks' ? 'tasks-page' : ''} ${(location.pathname === '/marketing' || location.pathname.startsWith('/marketing/')) ? 'marketing-page' : ''} ${location.pathname === '/automations' ? 'automations-page' : ''} ${location.pathname === '/online-store' ? 'online-store-page' : ''} ${location.pathname === '/stock' ? 'stock-page' : ''} ${location.pathname === '/sell-products' ? 'sell-products-page' : ''} ${location.pathname === '/invoices' ? 'invoices-page' : ''} ${location.pathname === '/expenses' ? 'expenses-page' : ''} ${(location.pathname.startsWith('/settings') || location.pathname.startsWith('/billing')) ? 'settings-page' : ''} ${location.pathname.startsWith('/crm/lead-forms') ? 'crm-lead-forms-page' : ''}`}
            data-page={
              location.pathname === '/messages' ? 'messages' :
              location.pathname === '/automations' ? 'automations' :
              undefined
            }
          >
            <Outlet context={{ tasksPermissionLevel: "manage" as const, automationsPermissionLevel: "manage" as const, settingsPermissionLevel }} />
          </main>
        </div>
      </div>

      {/* Show FloatingActionButton on all platforms - unified experience */}
      {location.pathname !== '/payroll/pincode' && (
      <FloatingActionButton
        permissionLevel={bookingsPermissionLevel}
        onOpenAISetupAssistant={isAdmin ? () => setIsAISetupAssistantOpen(true) : undefined}
        sidebarOpen={sidebarOpen}
        onSetSidebarOpen={setSidebarOpen}
        onBottomBarActiveChange={setBottomBarActive}
      />
      )}
      {/* A bot voice call that carries on outside its chat (press-and-hold on the AI button). */}
      <FloatingVoicePill />

      {/* Tasks drawers — AI + Control Centre badge open these from any screen */}
      <TasksAssistantHost />
      
      {/* Modals - moved outside of FloatingActionButton */}

      <BookingHelperModal
        isOpen={bookingHelper.isOpen}
        onClose={bookingHelper.close}
        issues={bookingHelper.issues}
      />

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => {
          console.log('📱 User manually closed CreateBookingModal, clearing localStorage');
          // clearModalData(); // Clear modal persistence data - REMOVED
          setIsCreateBookingModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('📱 CreateBookingModal success, clearing localStorage');
          // clearModalData(); // Clear modal persistence data - REMOVED
          setIsCreateBookingModalOpen(false);
        }}
      />

      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={async () => {
          setIsCreateProposalModalOpen(false);
        }}
      />

      <WithdrawStockModal
        isOpen={isWithdrawStockModalOpen}
        onClose={() => setIsWithdrawStockModalOpen(false)}
        onSuccess={async () => {
          setIsWithdrawStockModalOpen(false);
        }}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />

      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        onClose={() => {
          console.log('📱 User manually closed CreateMobileBookingModal, clearing localStorage');
          // clearMobileModalData(); // Clear modal persistence data - REMOVED
          setIsCreateMobileBookingModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('📱 CreateMobileBookingModal success, clearing localStorage');
          // clearMobileModalData(); // Clear modal persistence data - REMOVED
          setIsCreateMobileBookingModalOpen(false);
          
          // Dispatch custom event to refresh mobile calendar
          window.dispatchEvent(new CustomEvent('mobileBookingCreated'));
        }}
      />

      {/* Warranty Booking Modal */}
      <CreateBookingModal
        isOpen={isCreateWarrantyModalOpen}
        onClose={() => {
          console.log('🛡️ User manually closed WarrantyModal');
          setIsCreateWarrantyModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('🛡️ WarrantyModal success');
          setIsCreateWarrantyModalOpen(false);
          
          // Dispatch custom event to refresh bookings
          window.dispatchEvent(new CustomEvent('warrantyBookingCreated'));
        }}
        isWarrantyBooking={true}
      />

      {/* Nexus AI Assistant Overlay */}
      <NexusOverlay 
        isOpen={isNexusOverlayOpen} 
        onClose={() => setIsNexusOverlayOpen(false)}
      />

      {/* Global Help - How do I...? (uses AI token system) */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        onOpenSetupWizard={() => setIsAISetupAssistantOpen(true)}
      />

      {/* Shared AI onboarding for Help (Voice FAB mounts its own copy) */}
      <NexusVoiceIntroModal
        isOpen={showAiIntro}
        onClose={() => { setShowAiIntro(false); setAiPendingAction(null); }}
        onAccepted={() => {
          markAiOnboardingComplete();
          setAiPendingAction(null);
          setIsHelpModalOpen(true);
        }}
        businessId={selectedBusiness?.id}
        welcomeAlreadyClaimed={aiWelcomeClaimed}
        termsAlreadyAccepted={aiTermsAccepted}
      />

      {/* AI assistant's visual pointer (pulsing ring on highlighted elements) */}
      <AssistantHighlightOverlay />

      {/* AI assistant's candidate-customer "ghost" cards */}
      <AssistantGhostMatches />

      {/* Live team map, opened by the assistant ("where's Yusuf?") */}
      <TeamMapModalHost />

      {/* Nexus AI Set Up Assistant */}
      <AISetupAssistantModal
        isOpen={isAISetupAssistantOpen}
        onClose={() => {
          setIsAISetupAssistantOpen(false);
          setAiSetupResumeTarget(null);
        }}
        resumeTarget={aiSetupResumeTarget}
      />

      {/* Website/domain setup helper — auto-opens for the admin when a Vercel
          project is connected but hosting/custom-domain setup isn't finished. */}
      <DomainSetupHelperModal suppressed={isAISetupAssistantOpen} />

      {/* AI token auto top-up: failed-payment interrupt (shows wherever the user is) */}
      <AutoTopupFailedModal />

      {/* What's new after an update: blocks the app once per device until clicked through */}
      <AppUpdateModal />

    </div>
  );
});

// CRITICAL: Check if current URL is a public queue route BEFORE any React rendering
// This allows us to render queue pages WITHOUT any context providers
const isPublicQueueRoute = () => {
  const path = window.location.pathname;
  // Match: /:businessSlug/que, /:businessSlug/que/thank-you, /:businessSlug/que/status/:token
  // But NOT /que (which is the internal admin page)
  // Public routes have a business slug BEFORE /que, so path does NOT start with /que
  return path.includes('/que') && !path.startsWith('/que');
};

// Minimal App for public queue routes - NO CONTEXT PROVIDERS
const PublicQueueApp = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/:businessSlug/que/status/:token" element={<PublicQueueStatus />} />
        <Route path="/:businessSlug/que/thank-you" element={<QueueThankYou />} />
        <Route path="/:businessSlug/que" element={<PublicQue />} />
        {/* Fallback - redirect to home if somehow we got here incorrectly */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

// Full App with all providers for authenticated routes
const FullApp = () => {
  // Call our hook to enable auto-selection for all number inputs
  useSelectInputOnFocus();
  
  // Initialize mobile service for native app features
  useEffect(() => {
    mobileService.initialize();
  }, []);

  return (
    <>
    <SupabaseProvider>
        <BusinessProvider business={null}>
          <RemoteSessionProvider>
          <LocationTrackingDebugProvider>
          <ImageViewerProvider>
          <DataCacheProvider>
            <ApplicationSettingsProvider>
              <PermissionProvider>
                <BookingStatus.Provider>
                  <NotificationsProvider>
                    <SubscriptionProvider>
                      <TutorialProvider>
                        <UnreadMessagesProvider>
                          <UnreadOpportunitiesProvider>
                          <CallProvider>
                            <SimpleTimerProvider>
                              <AppBadgeManager>
                              <VoiceHelpProvider>
                              <BrowserRouter>
                              <AgentBetaProvider>
                                <>
                                <DeepLinkNavigator />
                                <GoogleBusinessOauthReturnListener />
                                <ToastProvider>
                                <Routes>
                                  {/* Public Routes - These must come FIRST to ensure they're not caught by auth logic */}
                                  <Route path="/login" element={<Login />} />
                                  <Route path="/signup" element={<Signup />} />
                                  <Route path="/sign-up-new-business" element={<SignupNewBusiness />} />
                                  <Route path="/Signup-nexus" element={<SignupNexus />} />
                                  <Route path="/Signup-nexus/email" element={<SignupNexusEmail />} />
                                  <Route path="/Signup-nexus/preparing" element={<SignupNexusPreparing />} />
                                  <Route path="/signup-nexus" element={<SignupNexus />} />
                                  <Route path="/signup-nexus/email" element={<SignupNexusEmail />} />
                                  <Route path="/signup-nexus/preparing" element={<SignupNexusPreparing />} />
                                  <Route path="/setup" element={<Setup />} />
                                  <Route path="/reset-password" element={<ResetPassword />} />
                                  <Route path="/reset-password/update" element={<UpdatePassword />} />
                                  <Route path="/account-disabled" element={<AccountDisabled />} />
                                  <Route path="/feedback/:token" element={<FeedbackPage />} />
                                  <Route path="/referral-partner/invite/:token" element={<ReferralPartnerInvite />} />
                                  <Route path="/liability-waiver/:token" element={<LiabilityWaiver />} />
                                  <Route path="/resource-invite/:token" element={<ResourceInviteAccept />} />
                                  {/* Native OAuth handoff — public https page Safari can open, then deep-links into the app */}
                                  <Route path="/oauth-app-return" element={<OauthAppReturn />} />

                                  <Route
                                    path="/receipt/:id"
                                    element={<PublicPDFReceipt />}
                                  />
                                  {/* CRITICAL: Public proposal route - must be BEFORE any wildcards */}
                                  <Route
                                    path="/proposal/:token"
                                    element={<PublicProposal />}
                                  />
                                  <Route path="/intake/:token" element={<PublicIntakeForm />} />
                                  <Route path="/:businessSlug/book/:portalSlug" element={<BookOnline />} />
                                  <Route path="/:businessSlug/book" element={<BookOnline />} />
                                  <Route path="/:businessSlug/que/status/:token" element={<PublicQueueStatus />} />
                                  <Route path="/:businessSlug/que" element={<PublicQue />} />
                                  <Route path="/:businessSlug/que/thank-you" element={<QueueThankYou />} />
                                  <Route path="/:businessSlug/booking-success" element={<BookingSuccess />} />
                                  <Route path="/:businessSlug/gift-cards" element={<BuyGiftCard />} />
                                  <Route path="/:businessSlug/gift-card-success" element={<GiftCardSuccess />} />
                                  <Route path="/:businessSlug/:formSlug" element={<PublicLeadForm />} />
                                  <Route path="/pay-invoice/:token/success" element={<PayInvoiceSuccess />} />
                                  <Route path="/pay-invoice/:token" element={<PayInvoice />} />
                                  <Route path="/subscribe/:token" element={<ActivateSubscription />} />
                                  <Route path="/:businessSlug/meet/:screenSlug" element={<MeetBook />} />
                                  <Route path="/:businessSlug/store" element={<StoreOnline />} />
                                  <Route path="/:businessSlug/portal" element={<CustomerPortal />} />
                                  <Route path="/book" element={<Navigate to="/" replace />} />
                                  
                                  {/* Subscription Management Routes */}
                                  <Route path="/admin-subscription-management" element={<AdminSubscriptionManagement />} />
                                  <Route path="/resource-subscription-expired" element={<ResourceSubscriptionExpired />} />
                                  <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                                  
                                  {/* Protected Routes */}
                                  <Route
                                    element={
                                      <AuthGuard>
                                        <SubscriptionGuard>
                                          <SetupGuard>
                                            <AppContent />
                                          </SetupGuard>
                                        </SubscriptionGuard>
                                      </AuthGuard>
                                    }
                                  >
                                    {/* Tutorial Route */}
                                    <Route path="/tutorial" element={
                                      <ErrorBoundary>
                                        <Tutorial />
                                      </ErrorBoundary>
                                    } />
                                    
                                    {/* Main App Routes */}
                                      {/* Home/Default Page */}
                                      <Route path="/home" element={
                                        <ErrorBoundary>
                                          <Home />
                                        </ErrorBoundary>
                                      } />
                                      
                                      {/* Root route - dynamically redirects based on user's home screen preference */}
                                      <Route index element={<HomeScreenRedirect />} />
                                      <Route path="/" element={<HomeScreenRedirect />} />
                                    {/* Bookings app retired → list view lives inside the Calendar app */}
                                    <Route path="/bookings" element={<Navigate to="/calendarV2" replace />} />
                                    <Route path="/proposals" element={
                                      <AppRouteGuard appSlug="proposals">
                                        <PermissionGuard permission="proposals.pageAccess">
                                          <Proposals />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/customers" element={
                                      <AppRouteGuard appSlug="customers">
                                        <PermissionGuard permission="customers.pageAccess">
                                          <Customers />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/documents" element={
                                      <AppRouteGuard appSlug="tasks">
                                        <PermissionGuard permission="tasks.pageAccess" pageId="tasks">
                                          <Documents />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                    <Route path="/payroll" element={
                      <AppRouteGuard appSlug="payroll">
                        <PermissionGuard permission="payroll.pageAccess">
                          <Payroll />
                        </PermissionGuard>
                      </AppRouteGuard>
                    } />
                    <Route path="/payroll/pincode" element={
                      <AppRouteGuard appSlug="payroll">
                        <ClockInPincode />
                      </AppRouteGuard>
                    } />
                    <Route path="/gift-cards" element={
                      <AppRouteGuard appSlug="gift-cards">
                        <GiftCards />
                      </AppRouteGuard>
                    } />
                    <Route path="/flat-rates" element={
                      <AppRouteGuard appSlug="payroll">
                        <PermissionGuard permission="payroll.pageAccess">
                          <FlatRates />
                        </PermissionGuard>
                      </AppRouteGuard>
                    } />
                                    <Route path="/stock" element={
                                      <AppRouteGuard appSlug="inventory">
                                        <Stock />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/invoices" element={
                                      <AppRouteGuard appSlug="invoices">
                                        <Invoices />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/sell-products" element={
                                      <AppRouteGuard appSlug="products">
                                        <SellProducts />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/online-store" element={
                                      <AppRouteGuard appSlug="products">
                                        <OnlineStore />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/inventory/materials" element={
                                      <AppRouteGuard appSlug="inventory">
                                        <InventoryMaterials />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/app-store" element={
                                      <ErrorBoundary>
                                        <AppStore />
                                      </ErrorBoundary>
                                    } />
                                    <Route path="/my-apps" element={
                                      <ErrorBoundary>
                                        <MyApps />
                                      </ErrorBoundary>
                                    } />
                                    <Route path="/websites" element={
                                      <ErrorBoundary>
                                        <Websites />
                                      </ErrorBoundary>
                                    } />
                                    <Route path="/websites/editor" element={<Navigate to="/websites" replace />} />
                                    <Route path="/resources" element={
                                      <AppRouteGuard appSlug="teams">
                                        <Resources />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/teams/permissions" element={
                                      <AppRouteGuard appSlug="teams">
                                        <TeamsResourcePermissions />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/mobile-calendar/online-booking" element={
                                      <AppRouteGuard appSlug="calendar">
                                        <MobileCalendarOnlineBooking />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/services" element={
                                      <AppRouteGuard appSlug="services">
                                        <Services />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/reports" element={<Reports />}>
                                      <Route path="sales" element={<Sales />} />
                                      <Route
                                        path="profit-loss"
                                        element={<ProfitLoss />}
                                      />
                                    </Route>
                                    <Route path="/settings/*" element={<Settings />} />
                                    <Route path="/business-settings" element={<BusinessSettings />} />
                                    <Route path="/ai-settings" element={<AISettings />} />
                                    <Route path="/agents" element={<AgentsPage />} />
                                    <Route path="/integrations" element={<LegacyIntegrationsRedirect />} />
                                    <Route path="/marketing" element={
                                      <AppRouteGuard appSlug="marketing">
                                        <PermissionGuard permission="marketing.pageAccess" pageId="marketing">
                                          <Marketing />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/workflows" element={<Workflows />} />
                                    <Route path="/que" element={
                                      <AppRouteGuard appSlug="que">
                                        <Que />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/reports" element={<Reports />} />
                                    <Route
                                      path="/sales-reports"
                                      element={<SalesReports />}
                                    >
                                      <Route path="sales" element={<Sales />} />
                                      <Route
                                        path="profit-loss"
                                        element={<ProfitLoss />}
                                      />
                                    </Route>
                                    <Route path="/team-reports" element={<TeamReports />} />
                                    {/* Tasks app retired → repurposed as Docs */}
                                    <Route path="/tasks" element={<Navigate to="/documents" replace />} />
                                    <Route path="/projects" element={
                                      <AppRouteGuard appSlug="projects">
                                        <PermissionGuard permission="projects.pageAccess" pageId="projects">
                                          <Projects />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/expenses" element={
                                      <AppRouteGuard appSlug="expenses">
                                        <Expenses />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/subscriptions" element={
                                      <AppRouteGuard appSlug="services">
                                        <Subscriptions />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/automations" element={
                                      <AppRouteGuard appSlug="automations">
                                        <PermissionGuard permission="automations.pageAccess">
                                          <AutomationsV2 />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/automations-v2" element={
                                      <PermissionGuard permission="automations.pageAccess">
                                        <AutomationsV2 />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/automations/agents/:agentId" element={
                                      <AppRouteGuard appSlug="automations">
                                        <PermissionGuard permission="automations.pageAccess">
                                          <ReceptionistBuilderPage />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/ai-agents" element={
                                      <AppRouteGuard appSlug="automations">
                                        <AIAgents />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/ai-agents/:agentId" element={
                                      <AppRouteGuard appSlug="automations">
                                        <AIAgentBuilder />
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/gps" element={<GPS />} />
                                    <Route path="/online-bookings" element={<Navigate to="/settings" state={{ from: '/online-bookings' }} replace />} />
                                    <Route path="/online-booking-editor" element={<OnlineBookingEditor />} />

                                    <Route path="/messages" element={
                                      <AppRouteGuard appSlug="messages">
                                        <PermissionGuard permission="messages.pageAccess">
                                          <ErrorBoundary componentName="Messages">
                                            <Messages />
                                          </ErrorBoundary>
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/calendarV2" element={
                                      <AppRouteGuard appSlug="calendar">
                                        <PermissionGuard permission="calendar.pageAccess">
                                          <CalendarV2 />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/mobile-calendar" element={
                                      <AppRouteGuard appSlug="mobile-calendar">
                                        <PermissionGuard permission="calendar.pageAccess">
                                          <MobileCalendar />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/supplies-usage" element={<SuppliesUsage />} />
                                    <Route path="/crm" element={
                                      <AppRouteGuard appSlug="crm">
                                        <PermissionGuard permission="crm.pageAccess">
                                          <CRM />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/crm/lead-forms" element={
                                      <AppRouteGuard appSlug="crm">
                                        <PermissionGuard permission="crm.pageAccess">
                                          <CrmLeadFormsPage />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/crm/lead-forms/:formId" element={
                                      <AppRouteGuard appSlug="crm">
                                        <PermissionGuard permission="crm.pageAccess">
                                          <LeadFormBuilderPage />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/profile" element={<Profile />} />
                                    <Route path="/test-notification" element={<TestNotification />} />
                                  <Route path="/rex-testing" element={<RexTesting />} />
                                  <Route path="/rex-training" element={<RexTraining />} />
                                  <Route path="/website" element={<Website />} />
                                    <Route path="/dialler" element={<Dialler />} />
                                    <Route path="/calls-beta" element={<CallsBeta />} />
                                    <Route path="/kpi-dashboard" element={
                                      <AppRouteGuard appSlug="analytics">
                                        <PermissionGuard permission="kpiDashboard.pageAccess">
                                          <KPIDashboard />
                                        </PermissionGuard>
                                      </AppRouteGuard>
                                    } />
                                    <Route path="/admin-dashboard" element={<AdminDashboard />} />
                                    <Route path="/super-admin" element={<SuperAdminDashboard />} />
                                  </Route>
                                </Routes>
                              </ToastProvider>
                            {/* Global AI Text Selection - disabled on login and other auth screens */}
                            <ConditionalGlobalTextSelectionAI />
                                </>
                              </AgentBetaProvider>
                            </BrowserRouter>
                            </VoiceHelpProvider>
                            {/* iOS App Store update banner (auto-detects new published version) */}
                            <AppUpdateBanner />
                            {/* App-level call components */}
                            <ActiveCallModal />
                            {!ImpersonationService.isImpersonating() && (
                              <>
                                <GlobalLocationTracker />
                                <NativePushRegistration />
                                <RemoteSessionGuard />
                              </>
                            )}
                            </AppBadgeManager>
                            </SimpleTimerProvider>
                          </CallProvider>
                          </UnreadOpportunitiesProvider>
                        </UnreadMessagesProvider>
                      </TutorialProvider>
                    </SubscriptionProvider>
                    </NotificationsProvider>
                </BookingStatus.Provider>
              </PermissionProvider>
            </ApplicationSettingsProvider>
          </DataCacheProvider>
          </ImageViewerProvider>
          </LocationTrackingDebugProvider>
          </RemoteSessionProvider>
        </BusinessProvider>
    </SupabaseProvider>
    <ToastContainer />
    </>
  );
};

// Only enable global AI text selection when not on auth screens (login, signup, etc.)
function ConditionalGlobalTextSelectionAI() {
  const location = useLocation();
  const isAuthRoute = /^\/(login|signup|sign-up-new-business|Signup-nexus|signup-nexus|setup|reset-password|account-disabled)(\/|$)/.test(location.pathname) ||
    location.pathname.startsWith('/reset-password/') ||
    location.pathname.includes('/meet/');
  if (isAuthRoute) return null;
  return <GlobalTextSelectionAI />;
}

// MAIN APP COMPONENT - decides which version to render based on URL
const App = () => {
  // For public queue routes, use minimal app WITHOUT context providers
  if (isPublicQueueRoute()) {
    return <PublicQueueApp />;
  }
  
  // For everything else, use full app with all providers
  return <FullApp />;
};

export default App;